import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  canonical?: string;
  type?: 'website' | 'article';
  author?: string;
  publishDate?: string;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  canonical,
  type = 'website',
  author,
  publishDate
}) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const siteUrl = 'https://premium.kexa.io';
  const defaultTitle = 'Kexa Premium - Advanced Cloud Security, Compliance & FinOps';
  const defaultDescription = 'Enterprise-grade cloud security monitoring with AI-powered compliance scanning. Monitor AWS, Azure, GCP with custom rules, visualization, and priority support.';
  const defaultKeywords = 'cloud security, compliance monitoring, AWS security, Azure security, GCP security, DevOps security, infrastructure scanning, security automation, compliance rules, cloud governance';
  const defaultImage = `${siteUrl}/og-image.png`;
  const finalTitle = title || defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;
  const finalImage = image || defaultImage;
  const finalCanonical = canonical || `${siteUrl}${location.pathname}`;
  const finalAuthor = author || 'Kexa Team';

  useEffect(() => {
    document.title = finalTitle;
    const updateMetaTag = (name: string, content: string, property?: string) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (element) {
        element.setAttribute('content', content);
      } else {
        element = document.createElement('meta');
        if (property) {
          element.setAttribute('property', name);
        } else {
          element.setAttribute('name', name);
        }
        element.setAttribute('content', content);
        document.head.appendChild(element);
      }
    };

    const updateLinkTag = (rel: string, href: string, hreflang?: string) => {
      const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
      let element = document.querySelector(selector) as HTMLLinkElement;
      
      if (element) {
        element.setAttribute('href', href);
      } else {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        element.setAttribute('href', href);
        if (hreflang) {
          element.setAttribute('hreflang', hreflang);
        }
        document.head.appendChild(element);
      }
    };
    updateMetaTag('description', finalDescription);
    updateMetaTag('keywords', finalKeywords);
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('viewport', 'width=device-width, initial-scale=1');
    updateMetaTag('theme-color', '#53adbb');
    updateMetaTag('language', i18n.language);
    if (finalAuthor) {
      updateMetaTag('author', finalAuthor);
    }
    updateMetaTag('og:title', finalTitle, true);
    updateMetaTag('og:description', finalDescription, true);
    updateMetaTag('og:image', finalImage, true);
    updateMetaTag('og:url', finalCanonical, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', 'Kexa Premium', true);
    updateMetaTag('og:locale', i18n.language === 'fr' ? 'fr_FR' : 'en_US', true);
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', finalTitle);
    updateMetaTag('twitter:description', finalDescription);
    updateMetaTag('twitter:image', finalImage);
    updateMetaTag('twitter:creator', '@Kexa4Cloud');
    updateLinkTag('canonical', finalCanonical);

    const supportedLanguages = ['en', 'fr', 'es', 'de'];
    supportedLanguages.forEach(lang => {
      const langCode = lang === 'en' ? 'en_EN' : lang === 'fr' ? 'fr_FR' : lang === 'es' ? 'es_ES' : 'de_DE';
      updateLinkTag('alternate', `${siteUrl}${location.pathname}?lang=${langCode}`, lang);
    });
    const generateStructuredData = () => {
      const baseData = {
        "@context": "https://schema.org",
        "@type": type === 'article' ? 'Article' : 'WebSite',
        "name": finalTitle,
        "description": finalDescription,
        "url": finalCanonical,
        "image": finalImage,
        "inLanguage": i18n.language,
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${siteUrl}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      };
      if (type === 'website') {
        return {
          ...baseData,
          "publisher": {
            "@type": "Organization",
            "name": "Kexa",
            "logo": {
              "@type": "ImageObject",
              "url": `${siteUrl}/logo.png`
            },
            "url": siteUrl,
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "email": "contact@kexa.io"
            }
          },
          "sameAs": [
            "https://github.com/4urcloud/kexa",
            "https://linkedin.com/company/kexa"
          ]
        };
      } else {
        return {
          ...baseData,
          "author": {
            "@type": "Person",
            "name": finalAuthor
          },
          "publisher": {
            "@type": "Organization",
            "name": "Kexa",
            "logo": {
              "@type": "ImageObject",
              "url": `${siteUrl}/logo.png`
            }
          },
          "datePublished": publishDate,
          "dateModified": publishDate
        };
      }
    };
    const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
    if (existingJsonLd) {
      existingJsonLd.remove();
    }
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(generateStructuredData());
    document.head.appendChild(script);
  }, [finalTitle, finalDescription, finalKeywords, finalImage, finalCanonical, finalAuthor, type, publishDate, location.pathname, i18n.language]);
  return null;
};

export default SEO;